import { Icon } from "best-common-react";
import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { BulkResolveNeedsReviewResponse, BulkResolvePersonDetail } from "../../../api/RosterApi";
import { PersonnelTypes } from "../../../constants/PersonnelTypes";
import { useGlobalModal } from "../../../contexts/GlobalModalContext";
import { OutlinedButtonStyled } from "../OutlinedButtonStyled";
import PersonnelProfileNewTabLink from "../PersonnelProfileNewTabLink";
import { PrimaryButtonStyled } from "../PrimaryButtonStyled";
import { formatBulkResolvePersonName } from "./bulkReviewResolve/bulkReviewResolveUtils";

const ModalBody = styled.div.attrs(() => ({
    className: "m-4",
}))`
    font-family: Helvetica;
    font-size: 14px;
    color: var(--slate);
    line-height: 1.5;
    max-height: 70vh;
    overflow-y: auto;
`;

const SectionBlock = styled.div`
    margin-bottom: 20px;
`;

const SectionTitle = styled.h6`
    font-family: Helvetica;
    font-size: 14px;
    font-weight: bold;
    color: var(--slate);
    margin-bottom: 10px;
`;

const LowEffortGrid = styled.div`
    display: flex;
    gap: 12px;
`;

const LowEffortColumn = styled.div`
    flex: 1;
    border: 1px solid var(--blue-grey);
    border-radius: 4px;
    padding: 10px 12px;
    min-height: 60px;
`;

const ColumnHeading = styled.div`
    font-weight: bold;
    font-size: 13px;
    color: #041e42;
`;

const ColumnHeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 8px;
`;

const CopyIconButton = styled.button`
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
    line-height: 1;

    &:disabled {
        cursor: default;
        opacity: 0.4;
    }
`;

const NameList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
`;

const NameListItem = styled.li`
    margin-bottom: 6px;
    font-size: 13px;
`;

const EmptyText = styled.div`
    font-size: 13px;
    color: var(--slate);
    font-style: italic;
`;

const AccordionItem = styled.div`
    border: 1px solid var(--blue-grey);
    border-radius: 4px;
    margin-bottom: 8px;
    overflow: hidden;
`;

const AccordionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    background-color: #dee5ed;
    cursor: pointer;
    font-family: Helvetica;
    font-size: 14px;
    color: #041e42;
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    gap: 0;
`;

const HeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    margin-left: 12px;
`;

const OutcomeLabel = styled.span`
    font-size: 12px;
    white-space: nowrap;
`;

const AccordionBody = styled.div`
    padding: 12px 14px;
    background-color: var(--cloud);
    border-top: 1px solid var(--blue-grey);
`;

const DetailRow = styled.div`
    margin-bottom: 8px;
    font-size: 13px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const DetailLabel = styled.strong`
    margin-right: 6px;
`;

const StyledIcon = styled(Icon)<{ color: string }>`
    color: ${props => props.color};
`;

type HighEffortKind = "failed" | "sos";

type HighEffortItem = {
    person: BulkResolvePersonDetail;
    kind: HighEffortKind;
};

export const formatBulkResolveActionLabel = (person: BulkResolvePersonDetail): string => {
    if (person.action === "RESTORE") {
        return "Restore";
    }
    if (person.action === "MOVE_TO_STATUS") {
        const target = person.targetStatus ?? "";
        if (
            target === "ACCEPT" ||
            target === "ACCEPT_AND_SEND_QUESTIONNAIRE" ||
            target === "EXEMPT" ||
            target === "ROLLOVER_IN_PROGRESS"
        ) {
            return target.replace(/_/g, " ");
        }
        return `Move to ${target}`;
    }
    return person.action || person.targetStatus || "Action";
};

export const formatBulkResolveStatusIdsLabel = (priorRosterStatusIds?: number[]): string => {
    const ids = priorRosterStatusIds ?? [];
    if (ids.length === 0) {
        return "";
    }
    return `[${ids.join(", ")}]`;
};

export const formatBulkResolveStatusIdsDisplay = (priorRosterStatusIds?: number[]): string => {
    const formatted = formatBulkResolveStatusIdsLabel(priorRosterStatusIds);
    return formatted || "None";
};

export const formatBulkResolveResultLabel = (kind: HighEffortKind): string =>
    kind === "failed" ? "Still in Needs Review, no change in status" : "Sent to SOS";

export const formatBulkResolveNameListCopyText = (persons: BulkResolvePersonDetail[]): string =>
    persons.map(person => formatBulkResolvePersonName(person)).join(" ");

export const formatBulkResolveExportHeaderLine = (person: BulkResolvePersonDetail): string => {
    const name = formatBulkResolvePersonName(person);
    return `${name} (id=${person.personnelId})`;
};

export const formatBulkResolveExportBlock = (
    person: BulkResolvePersonDetail,
    kind: HighEffortKind,
): string => {
    const lines = [
        `${formatBulkResolveExportHeaderLine(person)}`,
        `Action: ${formatBulkResolveActionLabel(person)}`,
    ];
    if (person.action === "RESTORE") {
        lines.push(`Status ids: ${formatBulkResolveStatusIdsDisplay(person.priorRosterStatusIds)}`);
    }
    if (person.errorMessage) {
        lines.push(`Error message: ${person.errorMessage}`);
    }
    lines.push(`Result: ${formatBulkResolveResultLabel(kind)}`);
    return lines.join("\n");
};

export const buildBulkResolveExportFilename = (): string => {
    const now = new Date();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    const yyyy = now.getFullYear();
    return `bulk-resolve-results-${mm}-${dd}-${yyyy}.txt`;
};

export const buildBulkResolveExportContent = (results: BulkResolveNeedsReviewResponse): string => {
    const failed = results.failed || [];
    const sentToSos = results.sentToSos || [];
    const blocks: string[] = [];

    failed.forEach(person => {
        blocks.push(formatBulkResolveExportBlock(person, "failed"));
    });
    sentToSos.forEach(person => {
        blocks.push(formatBulkResolveExportBlock(person, "sos"));
    });

    return blocks.join("\n---\n");
};

const downloadTextFile = (content: string, filename: string) => {
    const blob = new Blob([content], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }, 1000);
};

const LowEffortNameList: React.FC<{ persons: BulkResolvePersonDetail[] }> = ({ persons }) => {
    if (persons.length === 0) {
        return <EmptyText>Nothing to display</EmptyText>;
    }
    return (
        <NameList>
            {persons.map(person => (
                <NameListItem key={person.personnelId}>
                    <PersonnelProfileNewTabLink personnelId={person.personnelId}>
                        {formatBulkResolvePersonName(person)}
                    </PersonnelProfileNewTabLink>
                </NameListItem>
            ))}
        </NameList>
    );
};

const ColumnClipboardButton: React.FC<{ persons: BulkResolvePersonDetail[] }> = ({ persons }) => {
    const [copied, setCopied] = useState(false);
    const copyText = useMemo(() => formatBulkResolveNameListCopyText(persons), [persons]);

    const handleCopy = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (persons.length === 0) {
            return;
        }
        await navigator.clipboard.writeText(copyText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <CopyIconButton
            type="button"
            onClick={handleCopy}
            disabled={persons.length === 0}
            title={copied ? "Copied!" : "Copy to clipboard"}
        >
            <StyledIcon
                color={copied ? "green" : "var(--blue)"}
                iconName={copied ? "fa-check" : "fa-clipboard"}
            />
        </CopyIconButton>
    );
};

type BulkReviewResolveResultsModalBodyProps = {
    results: BulkResolveNeedsReviewResponse;
};

const BulkReviewResolveResultsModalBody: React.FC<BulkReviewResolveResultsModalBodyProps> = ({
    results,
}) => {
    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

    const safeResults = results ?? {
        restored: [],
        moved: [],
        failed: [],
        sentToSos: [],
    };

    const highEffortItems: HighEffortItem[] = useMemo(() => {
        const items: HighEffortItem[] = [];
        (safeResults.failed || []).forEach(person => items.push({ person, kind: "failed" }));
        (safeResults.sentToSos || []).forEach(person => items.push({ person, kind: "sos" }));
        return items;
    }, [safeResults.failed, safeResults.sentToSos]);

    const toggleExpanded = (key: string) => {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    if (!results) {
        return (
            <ModalBody>
                <p>No results to display.</p>
            </ModalBody>
        );
    }

    const movedCount = safeResults.moved?.length ?? 0;
    const restoredCount = safeResults.restored?.length ?? 0;
    const failedCount = safeResults.failed?.length ?? 0;
    const sosCount = safeResults.sentToSos?.length ?? 0;

    return (
        <ModalBody>
            <SectionBlock>
                <SectionTitle>
                    Successful — Moved ({movedCount}) · Restored ({restoredCount})
                </SectionTitle>
                <LowEffortGrid>
                    <LowEffortColumn>
                        <ColumnHeaderRow>
                            <ColumnHeading>Moved</ColumnHeading>
                            <ColumnClipboardButton persons={safeResults.moved || []} />
                        </ColumnHeaderRow>
                        <LowEffortNameList persons={safeResults.moved || []} />
                    </LowEffortColumn>
                    <LowEffortColumn>
                        <ColumnHeaderRow>
                            <ColumnHeading>Restored</ColumnHeading>
                            <ColumnClipboardButton persons={safeResults.restored || []} />
                        </ColumnHeaderRow>
                        <LowEffortNameList persons={safeResults.restored || []} />
                    </LowEffortColumn>
                </LowEffortGrid>
            </SectionBlock>

            <SectionBlock>
                <SectionTitle>
                    Needs attention — Failed ({failedCount}) · Sent to SOS ({sosCount})
                </SectionTitle>
                {highEffortItems.length === 0 ? (
                    <EmptyText>Nothing to display</EmptyText>
                ) : (
                    highEffortItems.map(({ person, kind }) => {
                        const itemKey = `${kind}-${person.personnelId}`;
                        const expanded = expandedKeys.has(itemKey);
                        const outcomeLabel =
                            kind === "failed" ? "Still in Needs Review" : "Sent to SOS";

                        return (
                            <AccordionItem key={itemKey}>
                                <AccordionHeader onClick={() => toggleExpanded(itemKey)}>
                                    <HeaderLeft>
                                        <PersonnelProfileNewTabLink
                                            personnelId={person.personnelId}
                                        >
                                            {formatBulkResolvePersonName(person) ||
                                                `Personnel ${person.personnelId}`}
                                        </PersonnelProfileNewTabLink>
                                        <span>&nbsp;(id={person.personnelId})</span>
                                    </HeaderLeft>
                                    <HeaderRight>
                                        <OutcomeLabel>{outcomeLabel}</OutcomeLabel>
                                        <StyledIcon
                                            color="#041e42"
                                            iconName={
                                                expanded ? "fa-chevron-up" : "fa-chevron-down"
                                            }
                                        />
                                    </HeaderRight>
                                </AccordionHeader>
                                {expanded ? (
                                    <AccordionBody>
                                        <DetailRow>
                                            <DetailLabel>Action:</DetailLabel>
                                            {formatBulkResolveActionLabel(person)}
                                        </DetailRow>
                                        {person.action === "RESTORE" ? (
                                            <DetailRow>
                                                <DetailLabel>Status ids:</DetailLabel>
                                                {formatBulkResolveStatusIdsDisplay(
                                                    person.priorRosterStatusIds,
                                                )}
                                            </DetailRow>
                                        ) : null}
                                        {person.errorMessage ? (
                                            <DetailRow>
                                                <DetailLabel>Error message:</DetailLabel>
                                                {person.errorMessage}
                                            </DetailRow>
                                        ) : null}
                                        <DetailRow>
                                            <DetailLabel>Result:</DetailLabel>
                                            {formatBulkResolveResultLabel(kind)}
                                        </DetailRow>
                                    </AccordionBody>
                                ) : null}
                            </AccordionItem>
                        );
                    })
                )}
            </SectionBlock>
        </ModalBody>
    );
};

type BulkReviewResolveResultsModalFooterProps = {
    results: BulkResolveNeedsReviewResponse;
    personnelAdvancedSearch(arg1: any, arg2: any, arg3: any, arg4: any): void;
    adminSelectedMenuOptions: Array<any>;
    adminAffiliationFilters: Array<any>;
    adminSearchText: Array<any> | string;
};

const BulkReviewResolveResultsModalFooter: React.FC<BulkReviewResolveResultsModalFooterProps> = ({
    results,
    personnelAdvancedSearch,
    adminSelectedMenuOptions,
    adminAffiliationFilters,
    adminSearchText,
}) => {
    const { closeModal } = useGlobalModal();

    const safeResults = results ?? {
        restored: [],
        moved: [],
        failed: [],
        sentToSos: [],
    };

    const canExport = (safeResults.failed?.length ?? 0) + (safeResults.sentToSos?.length ?? 0) > 0;

    const handleExport = () => {
        const content = buildBulkResolveExportContent(safeResults);
        downloadTextFile(content, buildBulkResolveExportFilename());
    };

    const handleClose = () => {
        closeModal();
        setTimeout(
            () =>
                personnelAdvancedSearch(
                    adminSearchText && adminSearchText.length !== 0 ? adminSearchText : null,
                    adminAffiliationFilters ? adminAffiliationFilters.map(opt => opt.orgId) : null,
                    adminSelectedMenuOptions.map(opt => opt.id),
                    PersonnelTypes.CLUB,
                ),
            500,
        );
    };

    return (
        <div className="d-flex justify-content-end w-100">
            <div className="mr-2">
                <OutlinedButtonStyled
                    width={120}
                    height={26}
                    disabled={!canExport}
                    onClick={handleExport}
                >
                    Export Failures
                </OutlinedButtonStyled>
            </div>
            <PrimaryButtonStyled width={80} height={26} onClick={handleClose}>
                Close
            </PrimaryButtonStyled>
        </div>
    );
};

BulkReviewResolveResultsModalBody.displayName = "BodyContent";
BulkReviewResolveResultsModalFooter.displayName = "FooterContent";

export { BulkReviewResolveResultsModalBody, BulkReviewResolveResultsModalFooter };
